import type { AIUsageFeature } from '@shared/ai/pricing';
import { supabase } from './supabase';

/**
 * The « Consommation » page (C2, 27/10/2026): what the workspace used in a
 * month. The cost is Domino's own figure, given only to platform operators;
 * a workspace manager gets the volumes with a null cost.
 */
export interface FeatureUsage {
  feature: AIUsageFeature;
  operations: number;
  calls: number;
  costMicroUsd: number | null;
}

export interface PersonUsage {
  userId: string;
  name: string;
  launches: number;
  nextActions: number;
  messages: number;
  costMicroUsd: number | null;
}

export interface LaunchUsage {
  executionId: string;
  title: string;
  isTest: boolean;
  name: string;
  startedAt: string;
  records: number;
  ownCostMicroUsd: number | null;
  followOnCostMicroUsd: number | null;
}

export interface MonthUsage {
  canSeeCost: boolean;
  features: FeatureUsage[];
  people: PersonUsage[];
  launches: LaunchUsage[];
}

export const FEATURE_LABELS: Record<AIUsageFeature, string> = {
  tool_run: 'Lancements d’outils',
  next_action: 'Prochaines actions',
  chat: 'Messages du chat',
  forge: 'Forge',
  qualification: 'Qualification',
  account_learning: 'Ce qu’on sait',
  lead_diagnostic: 'Diagnostics en ligne',
  outreach: 'Prospection',
  other: 'Autres',
};

const FEATURE_ORDER = Object.keys(FEATURE_LABELS) as AIUsageFeature[];

const UNKNOWN_PERSON = 'Membre sans nom affiché';

/** First day of the month, as the server reads it (YYYY-MM-01). */
export const monthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;

export const shiftMonth = (date: Date, months: number): Date => new Date(date.getFullYear(), date.getMonth() + months, 1);

export const monthLabel = (date: Date): string => date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

const toNumber = (value: unknown): number | null => (value === null || value === undefined ? null : Number(value));

export const loadMonthUsage = async (month: Date): Promise<MonthUsage> => {
  const p_month = monthKey(month);
  const [operator, features, people, launches] = await Promise.all([
    supabase.rpc('is_platform_operator'),
    supabase.rpc('read_ai_usage_features_c2', { p_month }),
    supabase.rpc('read_ai_usage_people_c2', { p_month }),
    supabase.rpc('read_ai_usage_launches_c2', { p_month }),
  ]);
  const failed = [operator, features, people, launches].find((result) => result.error);
  if (failed) throw new Error('La consommation n’est pas accessible pour le moment.');

  return {
    canSeeCost: operator.data === true,
    features: ((features.data ?? []) as Array<Record<string, unknown>>)
      .map((row) => ({
        feature: row.feature as AIUsageFeature,
        operations: Number(row.operations),
        calls: Number(row.calls),
        costMicroUsd: toNumber(row.cost_micro_usd),
      }))
      .sort((a, b) => FEATURE_ORDER.indexOf(a.feature) - FEATURE_ORDER.indexOf(b.feature)),
    people: ((people.data ?? []) as Array<Record<string, unknown>>)
      .map((row) => ({
        userId: String(row.user_id),
        name: (row.display_name as string | null)?.trim() || UNKNOWN_PERSON,
        launches: Number(row.launches),
        nextActions: Number(row.next_actions),
        messages: Number(row.messages),
        costMicroUsd: toNumber(row.cost_micro_usd),
      }))
      .sort((a, b) => (b.costMicroUsd ?? 0) - (a.costMicroUsd ?? 0) || b.launches - a.launches),
    launches: ((launches.data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      executionId: String(row.execution_id),
      title: String(row.title),
      isTest: row.is_test === true,
      name: (row.display_name as string | null)?.trim() || UNKNOWN_PERSON,
      startedAt: String(row.started_at),
      records: Number(row.records),
      ownCostMicroUsd: toNumber(row.own_cost_micro_usd),
      followOnCostMicroUsd: toNumber(row.follow_on_cost_micro_usd),
    })),
  };
};

const usd = (value: number, digits: number) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} $`;

/** Micro-dollars as read by a person: two decimals, or « < 0,01 $ » for a trace. */
export const formatCost = (micro: number | null): string => {
  if (micro === null || micro === 0) return '';
  const value = micro / 1e6;
  return value < 0.01 ? '< 0,01 $' : usd(value, 2);
};

/** Cost of one object of a launch: three decimals, a fiche costs a fraction of a cent. */
export const formatCostPer = (micro: number | null, count: number): string => {
  if (micro === null || micro === 0 || count <= 0) return '';
  return usd(micro / 1e6 / count, 3);
};
